/**
 * Iter XX.243 · Gully Pulse client wrapper.
 *
 * Thin fetch layer — never throws.
 *
 * Iter XX.267d — Real-device crash `undefined is not an object
 * (evaluating 'r.items.length')` traced to responses that DO return
 * a truthy body but ARE missing / null `items`. The old
 * `return data || fallback` pattern preserved the malformed body and
 * callers accessing `.items.length` blew up on physical iPhone.
 *
 * `_normItemsShape()` guarantees that every response passing through
 * this module has `items: Array` and safe scalar counters, regardless
 * of what the backend actually sent. Callers can now safely read
 * `.items.length`, `.items.map()` etc. without extra guards.
 *
 * The wrapper never invents rows — it only normalises the container.
 */
import api from "./api";

function _asArray(v) { return Array.isArray(v) ? v : []; }
function _asInt(v)   { return typeof v === "number" && Number.isFinite(v) ? v : 0; }
function _asBool(v)  { return v === true; }

/** Normalise ANY pulse response so `.items` is always an array. */
function _normItemsShape(data, extra = {}) {
  const d = (data && typeof data === "object") ? data : {};
  return {
    ...d,
    items:  _asArray(d.items),
    unread: _asInt(d.unread ?? extra.unread ?? 0),
    count:  _asInt(d.count  ?? extra.count  ?? 0),
    ...(extra.is_discover !== undefined
        ? { is_discover: _asBool(d.is_discover ?? extra.is_discover) }
        : {}),
  };
}

export async function fetchPulseFeed(limit = 30) {
  try {
    const { data } = await api.get("/pulse/feed", { params: { limit } });
    return _normItemsShape(data, { is_discover: false });
  } catch { return _normItemsShape(null, { is_discover: false }); }
}
export async function fetchPulseProfile(userId) {
  try { const { data } = await api.get(`/pulse/profile/${userId}`); return data; }
  catch { return null; }
}
export async function shareMoment({ kind, title, body, metadata }) {
  try { const { data } = await api.post("/pulse/moments", { kind, title, body, metadata }); return data; }
  catch (e) { return { error: e?.response?.data?.detail || "Couldn't share." }; }
}
export async function deleteMoment(momentId) {
  try { const { data } = await api.delete(`/pulse/moments/${momentId}`); return data; }
  catch { return { error: "Couldn't delete." }; }
}
export async function celebrateMoment(momentId, reaction = "fire") {
  try { const { data } = await api.post(`/pulse/moments/${momentId}/celebrate`, { reaction }); return data; }
  catch (e) { return { error: e?.response?.data?.detail || "Couldn't react." }; }
}
export async function followUser(userId) {
  try { const { data } = await api.post(`/pulse/follow/${userId}`); return data; }
  catch (e) { return { error: e?.response?.data?.detail || "Couldn't follow." }; }
}
export async function unfollowUser(userId) {
  try { const { data } = await api.delete(`/pulse/follow/${userId}`); return data; }
  catch { return { error: "Couldn't unfollow." }; }
}
export async function searchCricketers(q, limit = 20) {
  try {
    const { data } = await api.get("/pulse/search", { params: { q, limit } });
    return _normItemsShape(data);
  } catch { return _normItemsShape(null); }
}
export async function suggestCricketers(limit = 6) {
  try {
    const { data } = await api.get("/pulse/suggestions", { params: { limit } });
    return _normItemsShape(data);
  } catch { return _normItemsShape(null); }
}
export async function followUserFromDiscovery(userId, source = "discovery_search") {
  try { const { data } = await api.post(`/pulse/follow/${userId}`, null, { params: { source } }); return data; }
  catch (e) { return { error: e?.response?.data?.detail || "Couldn't follow." }; }
}
export async function fetchNotifications() {
  try {
    const { data } = await api.get("/pulse/notifications");
    return _normItemsShape(data);
  } catch { return _normItemsShape(null); }
}
export async function markNotificationsSeen() {
  try { await api.post("/pulse/notifications/mark_seen"); } catch { /* noop */ }
}
export async function fetchPulseEditableProfile() {
  try { const { data } = await api.get("/pulse/profile/me/editable"); return data; }
  catch { return null; }
}
export async function savePulsePublicProfile(updates) {
  try { const { data } = await api.put("/pulse/profile/me", updates); return data; }
  catch (e) { return { error: e?.response?.data?.detail || "Couldn't save." }; }
}
